import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || '';
const imgSrc = (p) => !p ? '' : /^https?:\/\//i.test(p) ? p : `${API_BASE}${p}`;

// ─── STAR RATING ──────────────────────────────────────────────────────────────
function Stars({ value, onChange, readOnly, size = '1.1rem' }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '0.2rem' }}>
      {[1,2,3,4,5].map(s => (
        <span key={s}
          onClick={() => !readOnly && onChange?.(s)}
          onMouseEnter={() => !readOnly && setHovered(s)}
          onMouseLeave={() => !readOnly && setHovered(0)}
          style={{ fontSize: size, cursor: readOnly ? 'default' : 'pointer', color: s <= (hovered || value) ? '#f59e0b' : 'var(--ink-5)', transition: 'color 0.2s' }}>★</span>
      ))}
    </div>
  );
}

// ─── ADMIN BLOG ───────────────────────────────────────────────────────────────
export function AdminBlog() {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, 'new' = new form, {post} = edit
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({
    title: '', slug: '', excerpt: '', content: '', category: 'interior-design',
    tags: '', author: 'Wedoo Interiors Team', metaTitle: '', metaDesc: '', metaKeywords: '',
    isPublished: false, featured: false, readTime: 5,
  });
  const [coverFile, setCoverFile] = useState(null);

  const load = () => {
    api.get('/blog/all').then(r => setPosts(Array.isArray(r.data)?r.data:(r.data?.posts||r.data?.data||[]))).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => {
    setForm({ title: '', slug: '', excerpt: '', content: '', category: 'interior-design', tags: '', author: 'Wedoo Interiors Team', metaTitle: '', metaDesc: '', metaKeywords: '', isPublished: false, featured: false, readTime: 5 });
    setCoverFile(null);
    setEditing('new');
  };

  const openEdit = (post) => {
    setForm({ ...post, tags: (post.tags || []).join(', '), isPublished: post.isPublished || false, featured: post.featured || false });
    setCoverFile(null);
    setEditing(post);
  };

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (coverFile) fd.append('image', coverFile);
      if (editing === 'new') {
        await api.post('/blog', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Post created!');
      } else {
        await api.patch(`/blog/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Post updated!');
      }
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try { await api.delete(`/blog/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const togglePublish = async (post) => {
    try {
      await api.patch(`/blog/${post._id}`, { isPublished: !post.isPublished });
      toast.success(post.isPublished ? 'Unpublished' : 'Published!');
      load();
    } catch { toast.error('Failed'); }
  };

  const CATEGORIES = ['interior-design','modular-kitchen','false-ceiling','home-lighting','tips-tricks','trends','knowledge-hub'];

  if (editing !== null) return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '2rem' }}>
        <button className="btn btn-dark" style={{ padding: '0.5rem 1rem', fontSize: '0.75rem' }} onClick={() => setEditing(null)}>← Back</button>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.6rem', color: 'var(--cream)' }}>
          {editing === 'new' ? 'New Blog Post' : 'Edit Post'}
        </h1>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="crm-detail-grid" style={{ gap: '2rem' }}>
          {/* Main */}
          <div>
            <div className="form-group">
              <label>Post Title *</label>
              <input value={form.title} onChange={e => { setForm(p => ({ ...p, title: e.target.value, slug: autoSlug(e.target.value) })); }} required placeholder="e.g. 5 Modular Kitchen Trends for 2030" />
            </div>
            <div className="form-group">
              <label>URL Slug *</label>
              <input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} required placeholder="auto-generated from title" />
            </div>
            <div className="form-group">
              <label>Excerpt * (shown in listing)</label>
              <textarea value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} required rows={3} placeholder="Brief description of the article..." />
            </div>
            <div className="form-group">
              <label>Content * (HTML supported)</label>
              <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} required rows={18}
                style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                placeholder={'<h2>Your heading</h2>\n<p>Your paragraph text...</p>\n<ul><li>Point one</li></ul>\n<blockquote>A key quote</blockquote>'} />
              <p style={{ color: 'var(--muted)', fontSize: '0.7rem', marginTop: '0.4rem' }}>Supports HTML tags: h2, h3, p, ul, ol, li, strong, em, blockquote, img, a, hr</p>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="card" style={{ padding: '1.5rem' }}>
              <p style={{ fontSize: '0.7rem', letterSpacing: '0.15em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '1rem' }}>Publish Settings</p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                <input type="checkbox" checked={form.isPublished} onChange={e => setForm(p => ({ ...p, isPublished: e.target.checked }))} />
                <span style={{ fontSize: '0.85rem', color: 'var(--cream)' }}>Published</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.featured} onChange={e => setForm(p => ({ ...p, featured: e.target.checked }))} />
                <span style={{ fontSize: '0.85rem', color: 'var(--cream)' }}>Featured Post</span>
              </label>
            </div>
            <div className="card" style={{ padding: '1.5rem' }}>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/-/g, ' ')}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Author</label>
                <input value={form.author} onChange={e => setForm(p => ({ ...p, author: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Read Time (minutes)</label>
                <input type="number" min="1" value={form.readTime} onChange={e => setForm(p => ({ ...p, readTime: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Tags (comma-separated)</label>
                <input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="modular kitchen, trends, 2030" />
              </div>
            </div>
            <div className="card" style={{ padding: '1.5rem' }}>
              <p style={{ fontSize: '0.7rem', letterSpacing: '0.15em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '1rem' }}>Cover Image</p>
              {(editing !== 'new' && editing?.coverImage) && (
                <img src={imgSrc(editing.coverImage)} alt="" style={{ width: '100%', height: '120px', objectFit: 'cover', marginBottom: '0.75rem' }} />
              )}
              <input type="file" accept="image/*" onChange={e => setCoverFile(e.target.files[0])} style={{ color: 'var(--muted)', fontSize: '0.78rem' }} />
            </div>
            <div className="card" style={{ padding: '1.5rem' }}>
              <p style={{ fontSize: '0.7rem', letterSpacing: '0.15em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '1rem' }}>SEO Settings</p>
              <div className="form-group">
                <label>Meta Title (60 chars)</label>
                <input value={form.metaTitle} onChange={e => setForm(p => ({ ...p, metaTitle: e.target.value }))} maxLength={60} />
              </div>
              <div className="form-group">
                <label>Meta Description (160 chars)</label>
                <textarea value={form.metaDesc} onChange={e => setForm(p => ({ ...p, metaDesc: e.target.value }))} maxLength={160} rows={3} />
              </div>
              <div className="form-group">
                <label>Meta Keywords</label>
                <input value={form.metaKeywords} onChange={e => setForm(p => ({ ...p, metaKeywords: e.target.value }))} />
              </div>
            </div>
            <button type="submit" className="btn btn-gold" style={{ justifyContent: 'center' }} disabled={saving}>
              {saving ? 'Saving...' : editing === 'new' ? '✦ Publish Post' : '✦ Update Post'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <p className="tag-label" style={{ marginBottom: '0.3rem' }}>Content</p>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', color: 'var(--cream)' }}>Blog Management</h1>
        </div>
        <button className="btn btn-gold" onClick={openNew}>+ New Post</button>
      </div>
      {loading ? <div className="admin-empty">Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {posts.length === 0 && <div className="admin-empty">No posts yet. Create your first blog post!</div>}
          {posts.map(p => (
            <div key={p._id} className="card" style={{ padding: '1.25rem', display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
              {p.coverImage && (
                <img src={imgSrc(p.coverImage)} alt={p.title} style={{ width: '80px', height: '60px', objectFit: 'cover', flexShrink: 0 }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                  <h4 style={{ color: 'var(--cream)', fontSize: '0.92rem' }}>{p.title}</h4>
                  <span className={`badge ${p.isPublished ? 'badge-green' : 'badge-muted'}`}>{p.isPublished ? 'Published' : 'Draft'}</span>
                  {p.featured && <span className="badge badge-gold">★ Featured</span>}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.72rem', color: 'var(--muted)' }}>
                  <span>{p.category?.replace(/-/g, ' ')}</span>
                  <span>•</span><span>{p.readTime} min</span>
                  <span>•</span><span>{p.views || 0} views</span>
                  {p.publishedAt && <><span>•</span><span>{new Date(p.publishedAt).toLocaleDateString()}</span></>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button className="btn btn-dark" style={{ padding: '0.4rem 0.8rem', fontSize: '0.72rem' }} onClick={() => togglePublish(p)}>
                  {p.isPublished ? 'Unpublish' : 'Publish'}
                </button>
                <button className="btn btn-gold" style={{ padding: '0.4rem 0.8rem', fontSize: '0.72rem' }} onClick={() => openEdit(p)}>Edit</button>
                <button onClick={() => handleDelete(p._id)} style={{ padding: '0.4rem 0.8rem', fontSize: '0.72rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius:'var(--radius)', color: '#f87171', cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ADMIN REVIEWS ────────────────────────────────────────────────────────────
export function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [rating, setRating]   = useState(5);
  const [form, setForm]       = useState({ name: '', location: '', projectType: '', source: 'google', sourceUrl: '', review: '', isVerified: true, isActive: true, featured: false });

  const load = () => { api.get('/reviews/all').then(r => setReviews(Array.isArray(r.data)?r.data:(r.data?.reviews||r.data?.data||[]))).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/reviews', { ...form, rating });
      toast.success('Review added!');
      setShowForm(false);
      setForm({ name: '', location: '', projectType: '', source: 'google', sourceUrl: '', review: '', isVerified: true, isActive: true, featured: false });
      setRating(5); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const toggle = async (id, field, val) => {
    try { await api.patch(`/reviews/${id}`, { [field]: val }); load(); } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/reviews/${id}`); load(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <p className="tag-label" style={{ marginBottom: '0.3rem' }}>Social Proof</p>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', color: 'var(--cream)' }}>Reviews Management</h1>
        </div>
        <button className="btn btn-gold" onClick={() => setShowForm(!showForm)}>{showForm ? '✕ Cancel' : '+ Add Review'}</button>
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Total Reviews', val: reviews.length },
          { label: 'Avg Rating', val: `${avgRating}★` },
          { label: 'Pending Approval', val: reviews.filter(r => !r.isActive).length },
          { label: 'Featured', val: reviews.filter(r => r.featured).length },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: 'var(--gold)' }}>{s.val}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.2rem', marginBottom: '1.5rem', color: 'var(--cream)' }}>Add Client Review</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid-2" style={{ gap: '1rem' }}>
              <div className="form-group"><label>Client Name *</label><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required /></div>
              <div className="form-group"><label>Location</label><input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Tumakuru" /></div>
              <div className="form-group"><label>Project Type</label>
                <select value={form.projectType} onChange={e => setForm(p => ({ ...p, projectType: e.target.value }))}>
                  <option value="">Select...</option>
                  {['Modular Kitchen','Interior Design','False Ceiling','Home Lighting','Aluminium & Glass','Complete Home'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Source</label>
                <select value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))}>
                  {['google','facebook','instagram','justdial','direct'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group"><label>Source URL (optional)</label><input value={form.sourceUrl} onChange={e => setForm(p => ({ ...p, sourceUrl: e.target.value }))} placeholder="https://g.co/..." /></div>
            <div className="form-group"><label>Star Rating *</label><Stars value={rating} onChange={setRating} size="1.8rem" /></div>
            <div className="form-group"><label>Review Text *</label><textarea value={form.review} onChange={e => setForm(p => ({ ...p, review: e.target.value }))} rows={4} required /></div>
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.25rem' }}>
              {[['isVerified','Verified'],['isActive','Active (Visible)'],['featured','Featured']].map(([field, label]) => (
                <label key={field} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--cream-2)' }}>
                  <input type="checkbox" checked={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.checked }))} />
                  {label}
                </label>
              ))}
            </div>
            <button type="submit" className="btn btn-gold" disabled={saving}>{saving ? 'Saving...' : 'Add Review'}</button>
          </form>
        </div>
      )}

      {loading ? <div className="admin-empty">Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {reviews.length === 0 && <div className="admin-empty">No reviews yet.</div>}
          {reviews.map(r => (
            <div key={r._id} className="card" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <strong style={{ color: 'var(--cream)', fontSize: '0.9rem' }}>{r.name}</strong>
                    {r.location && <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>📍 {r.location}</span>}
                    <Stars value={r.rating} readOnly size="0.85rem" />
                    <span className={`badge ${r.isActive ? 'badge-green' : 'badge-red'}`}>{r.isActive ? 'visible' : 'hidden'}</span>
                    {r.isVerified && <span className="badge badge-gold">verified</span>}
                    {r.featured && <span className="badge badge-gold">★ featured</span>}
                    <span className="badge badge-muted">{r.source}</span>
                  </div>
                  {r.projectType && <span className="badge badge-muted" style={{ marginBottom: '0.5rem', display: 'inline-block' }}>{r.projectType}</span>}
                  <p style={{ color: 'var(--cream-2)', fontSize: '0.83rem', lineHeight: '1.7' }}>"{r.review}"</p>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, flexWrap: 'wrap' }}>
                  <button className="btn btn-dark" style={{ padding: '0.35rem 0.6rem', fontSize: '0.68rem' }} onClick={() => toggle(r._id, 'isActive', !r.isActive)}>
                    {r.isActive ? 'Hide' : 'Show'}
                  </button>
                  <button className="btn btn-dark" style={{ padding: '0.35rem 0.6rem', fontSize: '0.68rem' }} onClick={() => toggle(r._id, 'featured', !r.featured)}>
                    {r.featured ? 'Unfeature' : 'Feature'}
                  </button>
                  <button className="btn btn-dark" style={{ padding: '0.35rem 0.6rem', fontSize: '0.68rem' }} onClick={() => toggle(r._id, 'isVerified', !r.isVerified)}>
                    {r.isVerified ? 'Unverify' : 'Verify'}
                  </button>
                  <button onClick={() => remove(r._id)} style={{ padding: '0.35rem 0.6rem', fontSize: '0.68rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius:'var(--radius)', color: '#f87171', cursor: 'pointer' }}>Del</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ADMIN FAQ ────────────────────────────────────────────────────────────────
export function AdminFAQ() {
  const [faqs, setFaqs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]       = useState({ question: '', answer: '', category: 'general', order: 0, isActive: true });

  const CATS = ['general','pricing','process','services','warranty','timeline'];

  const load = () => { api.get('/faqs/all').then(r => setFaqs(Array.isArray(r.data)?r.data:(r.data?.faqs||r.data?.data||[]))).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/faqs/${editingId}`, form);
        toast.success('FAQ updated!');
      } else {
        await api.post('/faqs', form);
        toast.success('FAQ added!');
      }
      setShowForm(false); setEditingId(null);
      setForm({ question: '', answer: '', category: 'general', order: 0, isActive: true });
      load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const startEdit = (faq) => {
    setForm({ question: faq.question, answer: faq.answer, category: faq.category, order: faq.order, isActive: faq.isActive });
    setEditingId(faq._id);
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/faqs/${id}`); load(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <p className="tag-label" style={{ marginBottom: '0.3rem' }}>SEO Content</p>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', color: 'var(--cream)' }}>FAQ Management</h1>
        </div>
        <button className="btn btn-gold" onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ question: '', answer: '', category: 'general', order: 0, isActive: true }); }}>
          {showForm && !editingId ? '✕ Cancel' : '+ Add FAQ'}
        </button>
      </div>

      <div style={{ padding: '1rem 1.5rem', background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius:'var(--radius)', marginBottom: '2rem' }}>
        <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
          <strong style={{ color: 'var(--gold)' }}>SEO Tip:</strong> FAQs are automatically rendered with FAQ Schema (JSON-LD) on the FAQ page, which helps Google show rich results and drives organic traffic to <strong>wedoointeriors.com/faq</strong>.
        </p>
      </div>

      {showForm && (
        <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.2rem', marginBottom: '1.5rem', color: 'var(--cream)' }}>{editingId ? 'Edit FAQ' : 'Add New FAQ'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="faq-form-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label>Question *</label>
                <input value={form.question} onChange={e => setForm(p => ({ ...p, question: e.target.value }))} required placeholder="e.g. How long does a modular kitchen take?" />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', paddingBottom: '1.35rem' }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
                  <span style={{ fontSize: '0.82rem', color: 'var(--cream-2)' }}>Active</span>
                </label>
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label>Answer *</label>
                <textarea value={form.answer} onChange={e => setForm(p => ({ ...p, answer: e.target.value }))} required rows={5} placeholder="Detailed, helpful answer..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="submit" className="btn btn-gold" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update FAQ' : 'Add FAQ'}</button>
              <button type="button" className="btn btn-dark" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="admin-empty">Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {faqs.length === 0 && <div className="admin-empty">No FAQs yet. Add your first FAQ to boost SEO!</div>}
          {faqs.map(f => (
            <div key={f._id} className="card" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <span className="badge badge-gold" style={{ fontSize: '0.6rem' }}>{f.category}</span>
                    <span className="badge badge-muted" style={{ fontSize: '0.6rem' }}>order: {f.order}</span>
                    <span className={`badge ${f.isActive ? 'badge-green' : 'badge-red'}`} style={{ fontSize: '0.6rem' }}>{f.isActive ? 'active' : 'hidden'}</span>
                  </div>
                  <p style={{ color: 'var(--cream)', fontSize: '0.88rem', fontWeight: '500', marginBottom: '0.4rem' }}>{f.question}</p>
                  <p style={{ color: 'var(--muted)', fontSize: '0.8rem', lineHeight: '1.6' }}>{f.answer.slice(0, 150)}{f.answer.length > 150 ? '...' : ''}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                  <button className="btn btn-gold" style={{ padding: '0.35rem 0.7rem', fontSize: '0.7rem' }} onClick={() => startEdit(f)}>Edit</button>
                  <button onClick={() => remove(f._id)} style={{ padding: '0.35rem 0.7rem', fontSize: '0.7rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius:'var(--radius)', color: '#f87171', cursor: 'pointer' }}>Del</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
